import { useTranslation } from "react-i18next";
import { Layers, AlertCircle } from "lucide-react";

interface Props {
  /** The saved schema. */
  schema: Record<string, unknown> | undefined;
  isEditing: boolean;
  /** The draft JSON text while editing. */
  text: string;
  onChange: (text: string) => void;
  /** i18n key of the sentence that says why the draft cannot be saved. */
  errorKey: string | null;
}

/** The product schema of an extraction recipe: the fields read for each product. */
export function ProductSchemaSection({ schema, isEditing, text, onChange, errorKey }: Props) {
  const { t } = useTranslation();
  const fields = Object.keys((schema?.properties as Record<string, unknown> | undefined) ?? {});

  return (
    <div className="bg-card rounded-lg border border-border">
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-medium text-foreground">{t("productSchema.title")}</h3>
        </div>
        <p className="text-sm text-muted-foreground">{t("productSchema.description")}</p>
      </div>
      <div className="p-4 space-y-3">
        {isEditing ? (
          <>
            <label htmlFor="product-schema" className="block text-sm font-medium text-foreground">
              {t("productSchema.label")}
            </label>
            <textarea
              id="product-schema"
              value={text}
              onChange={(e) => onChange(e.target.value)}
              spellCheck={false}
              className="w-full h-72 px-3 py-2 font-mono text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y bg-background"
            />
            <p className="text-xs text-muted-foreground">{t("productSchema.hint")}</p>
            {errorKey && (
              <p role="alert" className="flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {t(errorKey)}
              </p>
            )}
          </>
        ) : schema ? (
          <>
            <ul className="flex flex-wrap gap-2">
              {fields.map((field) => (
                <li key={field} className="px-2 py-0.5 text-xs font-mono rounded bg-muted">
                  {field}
                </li>
              ))}
            </ul>
            <pre className="text-sm font-mono whitespace-pre-wrap bg-muted rounded-lg p-4 overflow-x-auto">{JSON.stringify(schema, null, 2)}</pre>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">{t("productSchema.noSchema")}</p>
        )}
      </div>
    </div>
  );
}
